import { useCallback, useEffect, useState, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  fetchUserTrainings,
  fetchTrainingById,
  fetchTrainingsByDateRange,
  fetchTrainingsByLocation,
  fetchTrainingsByInstructor,
  fetchTrainingsByTechnique,
  searchUserTrainings,
  addNewTraining,
  updateExistingTraining,
  deleteExistingTraining,
  addPhotoToTraining,
  deletePhotoFromTraining,
  addVideoToTraining,
  deleteVideoFromTraining,
  updateTrainingVisibilitySettings,
  fetchTrainingStats,
  fetchMostUsedTechniques,
  clearCurrentTraining,
  clearError,
  clearPermissionError,
  clearTrainings,
  // Offline support actions
  setOnlineStatus,
  addPendingChange,
  // Action creators renamed in the slice
  addTrainingAction,
  updateTrainingAction,
  removeTraining
} from '../store/slices/trainingSlice';
import { 
  setupTrainingSync, 
  subscribeToTrainingUpdates,
  setupOfflineReconnection,
  forceSync
} from '../services/firebase/trainingSync.service';
import { useAuth } from './useAuth';
import logger from '../utils/logger';
import {
  TrainingInput,
  QueryOptions,
  Training
} from '../services/firebase/training.service';
import { 
  TrainingSessionData} from '../components/TrainingComponents/TrainingSession';
import {
  adaptFirebaseTrainingToSessionData,
  mapTrainingType,
  groupSessionsByDate,
  transformSessionsForCalendar,
  getTrainingSessionStatus
} from '../services/firebase/adapters/trainingAdapters';

export const useTraining = () => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const [syncSetup, setSyncSetup] = useState(false);
  
  const {
    trainings,
    trainingIds,
    totalResults,
    currentTraining,
    isLoading,
    error,
    permissionError,
    stats,
    mostUsedTechniques,
    lastQueryOptions,
    // Offline support
    isOnline,
    pendingChanges,
    lastSyncedAt
  } = useAppSelector((state) => state.training);
  
  // Monitor online status
  useEffect(() => {
    const handleOnline = () => {
      dispatch(setOnlineStatus(true));
    };
    
    const handleOffline = () => {
      dispatch(setOnlineStatus(false));
    };
    
    // Set initial online status
    dispatch(setOnlineStatus(navigator.onLine));
    
    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Clean up event listeners
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [dispatch]);
  
  // Set up Firebase sync when user is authenticated.
  // Keyed on user.uid only: with syncSetup in the deps, setSyncSetup(true)
  // re-ran the effect one render after setup and React's cleanup of the
  // previous run tore down the subscription that was just created.
  useEffect(() => {
    if (!user?.uid) {
      return;
    }

    let unsubscribe: (() => void) | null = null;
    let teardownOfflineReconnection: (() => void) | null = null;
    logger.info('Setting up training sync for user:', user.uid);

    // Always disable sample data generation
    localStorage.setItem('disableSampleData', 'true');
    localStorage.setItem('useCurrentDates', 'true');

    try {
      unsubscribe = setupTrainingSync(user.uid, dispatch);

      teardownOfflineReconnection = setupOfflineReconnection(user.uid, dispatch);
      setSyncSetup(true);
      logger.info('Training sync setup completed');
    } catch (error) {
      logger.error('Error setting up training sync:', error);
    }

    return () => {
      if (unsubscribe) {
        logger.info('Cleaning up training sync');
        unsubscribe();
      }
      if (teardownOfflineReconnection) {
        teardownOfflineReconnection();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, dispatch]);
  
  // Subscribe to a specific training for real-time updates
  const subscribeToTraining = useCallback((trainingId: string) => {
    return subscribeToTrainingUpdates(trainingId, dispatch);
  }, [dispatch]);

  // Get user trainings
  const getUserTrainings = useCallback(
    (uid: string, options?: QueryOptions) => {
      return dispatch(fetchUserTrainings({ uid, options })).unwrap();
    },
    [dispatch]
  );

  // Get training by ID
  const getTrainingById = useCallback(
    (trainingId: string) => {
      return dispatch(fetchTrainingById(trainingId)).unwrap();
    },
    [dispatch]
  );

  // Get trainings by date range
  const getTrainingsByDateRange = useCallback(
    (uid: string, startDate: number, endDate: number) => {
      return dispatch(fetchTrainingsByDateRange({ uid, startDate, endDate })).unwrap();
    },
    [dispatch]
  );

  // Get trainings by location
  const getTrainingsByLocation = useCallback(
    (uid: string, locationId: string) => {
      return dispatch(fetchTrainingsByLocation({ uid, locationId })).unwrap();
    },
    [dispatch]
  );

  // Get trainings by instructor
  const getTrainingsByInstructor = useCallback(
    (uid: string, instructorId: string) => {
      return dispatch(fetchTrainingsByInstructor({ uid, instructorId })).unwrap();
    },
    [dispatch]
  );

  // Get trainings by technique
  const getTrainingsByTechnique = useCallback(
    (uid: string, techniqueId: string) => {
      return dispatch(fetchTrainingsByTechnique({ uid, techniqueId })).unwrap();
    },
    [dispatch]
  );

  // Search trainings
  const searchTrainings = useCallback(
    (uid: string, searchText: string) => {
      return dispatch(searchUserTrainings({ uid, searchText })).unwrap();
    },
    [dispatch]
  );

  // Add training
  const addTraining = useCallback(
    (data: TrainingInput) => {
      return dispatch(addNewTraining(data)).unwrap();
    },
    [dispatch]
  );

  // Update training
  const updateTraining = useCallback(
    (id: string, data: Partial<Training>) => {
      return dispatch(updateExistingTraining({ id, data })).unwrap();
    },
    [dispatch]
  );

  // Delete training
  const deleteTraining = useCallback(
    (id: string) => {
      return dispatch(deleteExistingTraining(id)).unwrap();
    },
    [dispatch]
  );

  // Add photo to training
  const addPhoto = useCallback(
    (trainingId: string, file: File, caption?: string, aspectRatio?: number) => {
      return dispatch(addPhotoToTraining({ trainingId, file, caption, aspectRatio })).unwrap();
    },
    [dispatch]
  );

  // Delete photo from training
  const deletePhoto = useCallback(
    (trainingId: string, photoId: string) => {
      return dispatch(deletePhotoFromTraining({ trainingId, photoId })).unwrap();
    },
    [dispatch]
  );

  // Add video to training
  const addVideo = useCallback(
    (trainingId: string, youtubeId: string, title?: string) => {
      return dispatch(addVideoToTraining({ trainingId, youtubeId, title })).unwrap();
    },
    [dispatch]
  );

  // Delete video from training
  const deleteVideo = useCallback(
    (trainingId: string, videoId: string) => {
      return dispatch(deleteVideoFromTraining({ trainingId, videoId })).unwrap();
    },
    [dispatch]
  );

  // Update training visibility
  const updateVisibility = useCallback(
    (trainingId: string, isPublic?: boolean, shownOnFeed?: boolean) => {
      return dispatch(updateTrainingVisibilitySettings({ trainingId, isPublic, shownOnFeed })).unwrap();
    },
    [dispatch]
  );

  // Like functionality removed - use the unified useLikes hook instead

  // Get training stats
  const getTrainingStats = useCallback(
    (uid: string) => {
      return dispatch(fetchTrainingStats(uid)).unwrap();
    },
    [dispatch]
  );

  // Get most used techniques
  const getMostUsedTechniques = useCallback(
    (uid: string, limit?: number) => {
      return dispatch(fetchMostUsedTechniques({ uid, limit })).unwrap();
    },
    [dispatch]
  );

  // Clear current training
  const clearCurrentTrainingData = useCallback(() => {
    dispatch(clearCurrentTraining());
  }, [dispatch]);

  // Clear error
  const clearTrainingError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Clear permission error
  const clearTrainingPermissionError = useCallback(() => {
    dispatch(clearPermissionError());
  }, [dispatch]);

  // Clear trainings
  const clearAllTrainings = useCallback(() => {
    dispatch(clearTrainings());
  }, [dispatch]);

  // Like functionality removed - use the unified useLikes hook instead

  // Get a training from the store by ID
  const getTrainingFromStore = useCallback(
    (trainingId: string) => {
      return trainings[trainingId] || null;
    },
    [trainings]
  );

  // Get all trainings as an array
  const getAllTrainings = useCallback(() => {
    return trainingIds.map(id => trainings[id]);
  }, [trainingIds, trainings]);
  
  // Get all trainings as TrainingSessionData for UI
  const getTrainingSessionsData = useCallback(() => {
    // Use Object.keys(trainings) instead of trainingIds to ensure we get data
    // even if trainingIds array hasn't been populated yet
    const ids = trainingIds.length > 0 ? trainingIds : Object.keys(trainings);

    const sessions = ids
      .map(id => {
        const training = trainings[id];
        if (!training) return null;
        return adaptFirebaseTrainingToSessionData(training);
      })
      .filter(Boolean) as TrainingSessionData[];

    // Sort by date in descending order (newest first)
    return sessions.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA; // Descending order
    });
  }, [trainingIds, trainings]);
  
  // Get current training as TrainingSessionData for UI
  const getCurrentTrainingSessionData = useMemo(() => {
    if (!currentTraining) return null;
    return adaptFirebaseTrainingToSessionData(currentTraining);
  }, [currentTraining]);
  
  // Get calendar data for UI visualizations
  const getCalendarData = useCallback(() => {
    const sessions = getTrainingSessionsData();
    return transformSessionsForCalendar(sessions);
  }, [getTrainingSessionsData]);
  
  // Group training sessions by date for UI
  const getSessionsByDate = useCallback(() => {
    const sessions = getTrainingSessionsData();
    return groupSessionsByDate(sessions);
  }, [getTrainingSessionsData]);

  // Create a training with offline support
  const addTrainingWithOfflineSupport = useCallback(
    async (data: TrainingInput) => {
      try {
        if (!isOnline) {
          // Store the action to be processed when back online
          const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          
          // Add to pending changes
          dispatch(addPendingChange({
            type: 'create',
            entityId: tempId,
            data
          }));
          
          // Create an optimistic entry in the store
          const optimisticTraining: Training = {
            id: tempId,
            ...data,
            dateCreated: Date.now(),
            _offline: true,
            _pendingSync: true
          } as Training;
          
          // Add the optimistic entry to the store
          dispatch(addTrainingAction(optimisticTraining));
          
          return optimisticTraining;
        } else {
          // Online - use regular flow
          return await addTraining(data);
        }
      } catch (error) {
        // If there's an error even when online, store as pending
        if (isOnline) {
          dispatch(addPendingChange({
            type: 'create',
            entityId: 'retry',
            data
          }));
        }
        throw error;
      }
    },
    [isOnline, dispatch, addTraining]
  );
  
  // Update a training with offline support
  const updateTrainingWithOfflineSupport = useCallback(
    async (id: string, data: Partial<Training>) => {
      try {
        if (!isOnline) {
          // Store the action to be processed when back online
          dispatch(addPendingChange({
            type: 'update',
            entityId: id,
            data
          }));
          
          // Update the local copy optimistically
          const currentData = trainings[id];
          if (currentData) {
            const optimisticUpdate = {
              ...currentData,
              ...data,
              _offline: true,
              _pendingSync: true
            };
            
            dispatch(updateTrainingAction({ id, data: optimisticUpdate }));
            return optimisticUpdate;
          }
          throw new Error('Training not found in local store');
        } else {
          // Online - use regular flow
          return await updateTraining(id, data);
        }
      } catch (error) {
        // If there's an error even when online, store as pending
        if (isOnline) {
          dispatch(addPendingChange({
            type: 'update',
            entityId: id,
            data
          }));
        }
        throw error;
      }
    },
    [isOnline, dispatch, updateTraining, trainings]
  );
  
  // Delete a training with offline support
  const deleteTrainingWithOfflineSupport = useCallback(
    async (id: string) => {
      try {
        if (!isOnline) {
          // Store the action to be processed when back online
          dispatch(addPendingChange({
            type: 'delete',
            entityId: id,
            data: { id }
          }));
          
          // Remove locally
          dispatch(removeTraining(id));
          return id;
        } else {
          // Online - use regular flow
          return await deleteTraining(id);
        }
      } catch (error) {
        // If there's an error even when online, store as pending
        if (isOnline) {
          dispatch(addPendingChange({
            type: 'delete',
            entityId: id,
            data: { id }
          }));
        }
        throw error;
      }
    },
    [isOnline, dispatch, deleteTraining]
  );
  
  // Force a sync of all data with the server
  const forceSyncData = useCallback(async () => {
    if (!user) {
      logger.error('Cannot force sync - user not authenticated');
      return;
    }
    
    try {
      logger.info('Starting forced sync operation with configuration:');
      logger.info('- User:', user.uid);
      logger.info('- Online status:', isOnline ? 'ONLINE' : 'OFFLINE');
      logger.info('- Current trainings count:', Object.keys(trainings).length);
      
      // Always disable sample data generation and use current dates
      localStorage.setItem('useCurrentDates', 'true');
      localStorage.setItem('disableSampleData', 'true');
      
      // First try to get data with normal query
      if (isOnline) {
        logger.info('Device is online - attempting normal sync');

        // Get the current state for the sync operation
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _syncState = {
          trainings,
          trainingIds,
          pendingChanges: pendingChanges || []
        };
        
        // Pass the local state to avoid circular dependencies
        await forceSync(user.uid, dispatch);
      } else {
        logger.info('Device is offline - cannot sync with server');
        
        // Even if offline, we can try to generate sample data locally
        if (Object.keys(trainings).length === 0) {
          logger.info('No training data found - will generate sample data on next connection');
        }
      }
    } catch (error) {
      logger.error('Error forcing sync:', error);
    }
  }, [user, isOnline, dispatch, trainings, trainingIds, pendingChanges]);
  
  return {
    // State
    trainings,
    trainingIds,
    totalResults,
    currentTraining,
    isLoading,
    error,
    permissionError,
    stats,
    mostUsedTechniques,
    lastQueryOptions,
    syncSetup,
    // Offline support state
    isOnline,
    pendingChanges,
    lastSyncedAt,

    // Actions
    getUserTrainings,
    getTrainingById,
    getTrainingsByDateRange,
    getTrainingsByLocation,
    getTrainingsByInstructor,
    getTrainingsByTechnique,
    searchTrainings,
    addTraining,
    updateTraining,
    deleteTraining,
    // Offline-aware actions
    addTrainingWithOfflineSupport,
    updateTrainingWithOfflineSupport,
    deleteTrainingWithOfflineSupport,
    // Other actions
    addPhoto,
    deletePhoto,
    addVideo,
    deleteVideo,
    updateVisibility,
    getTrainingStats,
    getMostUsedTechniques,
    clearCurrentTrainingData,
    clearTrainingError,
    clearTrainingPermissionError,
    clearAllTrainings,

    // Firebase Sync
    subscribeToTraining,
    forceSyncData,

    // Helpers
    getTrainingFromStore,
    getAllTrainings,
    
    // UI Adapters
    getTrainingSessionsData,
    getCurrentTrainingSessionData,
    getCalendarData,
    getSessionsByDate,
    
    // Utility functions from adapters
    adaptFirebaseTrainingToSessionData,
    mapTrainingType,
    groupSessionsByDate,
    transformSessionsForCalendar,
    getTrainingSessionStatus
  };
}